import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './EmailModal.css';

const EMAIL_ENDPOINT = 'https://subscribe-mezcy6bvqq-uc.a.run.app';

const EmailModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Animation variants
  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    }
  };

  const formVariants = {
    initial: { opacity: 0, y: 20, height: 0 },
    animate: { 
      opacity: 1,
      y: 0,
      height: "auto",
      transition: {
        duration: 0.4,
        ease: "easeOut",
        when: "beforeChildren"
      }
    },
    exit: {
      opacity: 0,
      y: 20,
      height: 0,
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    }
  };

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden';
      // Clear any previous error messages and success state
      setError('');
      setIsSuccess(false);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Restore scrolling when modal is closed
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Auto-close timer for success state
  useEffect(() => {
    let timer;
    if (isSuccess) {
      timer = setTimeout(() => {
        onClose();
      }, 10000);
    }
    return () => clearTimeout(timer);
  }, [isSuccess, onClose]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(EMAIL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Clear the form and show success state
        setEmail('');
        setIsSuccess(true);
      } else {
        setError(data.error || 'Subscription failed. Please try again.');
      }
    } catch (error) {
      setError('Connection failed. Please check your internet and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Stop propagation on modal content click
  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="modal-content"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={handleContentClick}
          >
            <div className="modal-header">
              <div className="subscribe-text">Subscribe</div>
              <button className="close-button" onClick={onClose}>×</button>
            </div>

            <div className="modal-body">
              <AnimatePresence mode="wait">
                <motion.div
                  key={isSuccess ? 'success' : 'initial'}
                  variants={contentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <h1>
                    {isSuccess ? 'BOOM!' : 'STOP LURKING'}<br />
                    <span className="highlight">{isSuccess ? 'YOU\'RE IN' : 'YOU\'RE ONE OF US'}</span>
                  </h1>
                  <h2>{isSuccess ? 'Creative anarchy, incoming.' : 'Join the RawOra rebellion.'}</h2>
                  <p className={`description ${isSuccess ? 'success' : ''}`}>
                    {isSuccess 
                      ? 'Now sit back and wait for the good, weird stuff to hit your inbox.'
                      : 'Get the loudest, weirdest, realest updates straight from the creative underground. New drops, featured creators, chaotic brilliance—zero spam, all soul. No filters. No fluff. Just the good, raw sh*t.'
                    }
                  </p>
                </motion.div>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                {!isSuccess && (
                  <motion.form
                    onSubmit={handleSubmit}
                    className="email-form"
                    variants={formVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <div className="input-group">
                      <input
                        type="email"
                        placeholder="ENTER EMAIL HERE"
                        required
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setError('');
                        }}
                        className="email-input"
                        disabled={isSubmitting}
                      />
                      <button type="submit" className="submit-button" disabled={isSubmitting}>
                        {isSubmitting ? 'SUBSCRIBING...' : 'HECK YEAH, I\'M IN!'}
                      </button>
                    </div>
                    <AnimatePresence mode="wait">
                      {error && (
                        <motion.div
                          className="error-message"
                          initial={{ opacity: 0, height: 0, marginTop: 0 }}
                          animate={{ opacity: 1, height: "auto", marginTop: "1rem" }}
                          exit={{ opacity: 0, height: 0, marginTop: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                          {error}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EmailModal; 